import React, { useState, useEffect } from "react";
import "./CreateAgentModal.css";
import { useToast } from "../../components/Toast/ToastContainer";
import ConfirmCreateModal from "../ConfirmCreateModal/ConfirmCreateModal";

export default function CreateAgentModal({ isOpen, onClose, onSubmit }) {
  const toast = useToast();

  // State Form
  const [form, setForm] = useState({
    isCenter: false,
    cifParent: "",
    cifParentName: "",

    cifPayment: "",
    paymentName: "",
    agentId: "", // Auto-generated

    account: "",
    status: "",
    agentPhone: "",

    region: "",
    address: "",

    repCif: "",
    repName: "",
    repPhone: "",

    managerCode: "",
    managerName: "",
    dvkdCode: "",
    dvkdName: "",

    createdBy: "",
    createdAt: "",

    limitDay: "",
    limitMonth: "",

    registrationNumber: "",
  });

  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [accountOptions, setAccountOptions] = useState([]);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  // --- EFFECT 1: INIT DATA ---
  useEffect(() => {
    if (isOpen) {
      const currentUser = localStorage.getItem("full_name") || "admin";
      const now = new Date();
      const formattedDate = `${String(now.getDate()).padStart(2, "0")}/${String(
        now.getMonth() + 1
      ).padStart(2, "0")}/${now.getFullYear()} ${String(
        now.getHours()
      ).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(
        now.getSeconds()
      ).padStart(2, "0")}`;

      setForm({
        isCenter: false,
        cifParent: "",
        cifParentName: "",
        cifPayment: "",
        paymentName: "",
        agentId: "",
        account: "",
        status: "",
        agentPhone: "",
        region: "",
        address: "",
        repCif: "",
        repName: "",
        repPhone: "",
        managerCode: "",
        managerName: "",
        dvkdCode: "",
        dvkdName: "",
        createdBy: currentUser,
        createdAt: formattedDate,
        limitDay: "",
        limitMonth: "",
        registrationNumber: "DKKD-" + Math.floor(Math.random() * 10000),
      });
      setFiles([]);
      setAccountOptions([]);
      setIsConfirmOpen(false);
    }
  }, [isOpen]);

  // --- EFFECT 2: CALL API GENERATE AGENT ID ---
  useEffect(() => {
    const fetchAgentCode = async () => {
      // Chỉ gọi API khi có đủ 2 thông tin: CIF Chủ quản và Mã ĐVKD
      const { cifParent, dvkdCode } = form;

      if (cifParent && dvkdCode) {
        try {
          const token = localStorage.getItem("access_token");
          const url = `https://api.bvb.cybertech.com.vn/api/v1/admin/agents/lookup/preview/agent-code?parent_cif=${cifParent}&business_unit_code=${dvkdCode}`;

          const response = await fetch(url, {
            method: "GET",
            headers: {
              "accept": "application/json",
              "Authorization": `Bearer ${token}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            // Cập nhật agentId từ response của API
            if (data.agent_code) {
              update("agentId", data.agent_code);
            }
          } else {
            // Nếu lỗi (ví dụ CIF ko tồn tại), xóa agentId hiện tại
            update("agentId", "");
          }
        } catch (error) {
          console.error("Lỗi khi lấy mã Agent Code:", error);
          update("agentId", "");
        }
      } else {
        // Nếu thiếu 1 trong 2 trường thì xóa mã cũ
        if (form.agentId !== "") {
          update("agentId", "");
        }
      }
    };

    // Debounce nhẹ để tránh gọi API liên tục khi đang gõ
    const timer = setTimeout(() => {
      fetchAgentCode();
    }, 500);

    return () => clearTimeout(timer);
  }, [form.cifParent, form.dvkdCode]); // Lắng nghe sự thay đổi của 2 trường này

  // --- HELPER CALL API ---
  const callApi = async (url, method = "GET", body = null) => {
    const token = localStorage.getItem("access_token");
    const headers = { Authorization: `Bearer ${token}` };

    // Chỉ thêm Content-Type JSON nếu không phải FormData
    if (body && !(body instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    try {
      const res = await fetch(url, {
        method,
        headers,
        body:
          body instanceof FormData ? body : body ? JSON.stringify(body) : null,
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || "Lỗi gọi API");
      }
      return data;
    } catch (error) {
      console.error(error);
      toast.error(error.message);
      return null;
    }
  };

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  // --- LOOKUP FUNCTIONS ---
  const handleLookupCifParent = async () => {
    if (!form.cifParent) return toast.warning("Vui lòng nhập CIF Chủ quản");
    const data = await callApi(
      `https://api.bvb.cybertech.com.vn/api/v1/admin/agents/lookup/parent-cif/${form.cifParent}`
    );
    if (data) {
      setForm((prev) => ({
        ...prev,
        cifParentName: data.agent_name,
        address: data.address,
      }));
      toast.success("Đã tìm thấy Đại lý chủ quản");
    }
  };

  const handleLookupCifPayment = async () => {
    if (!form.cifPayment) return toast.warning("Vui lòng nhập CIF Thanh toán");

    // 1. Gọi API lấy thông tin đại lý
    const agentData = await callApi(
      `https://api.bvb.cybertech.com.vn/api/v1/admin/agents/lookup/payment-cif/${form.cifPayment}`
    );

    if (agentData) {
      // CHỈ lưu trữ tên vào một biến tạm hoặc state ẩn nếu cần, 
      // nhưng CHƯA update vào form.paymentName để tránh hiển thị lên UI sớm.
      setForm((prev) => ({
        ...prev,
        tempPaymentName: agentData.agent_name, // Lưu tạm vào đây
        account: "",
        paymentName: "", // Đảm bảo tên vẫn trống
      }));

      // 2. Gọi API lấy danh sách tài khoản
      const accountsData = await callApi(
        `https://api.bvb.cybertech.com.vn/api/v1/admin/agents/accounts/${form.cifPayment}`
      );

      if (accountsData && Array.isArray(accountsData)) {
        setAccountOptions(accountsData);
        toast.success(`Tìm thấy ${accountsData.length} tài khoản giao dịch`);
      }
    }
  };

  const handleLookupRepCif = async () => {
    if (!form.repCif) return toast.warning("Vui lòng nhập CIF Người đại diện");
    const data = await callApi(
      `https://api.bvb.cybertech.com.vn/api/v1/admin/agents/lookup/representative/${form.repCif}`
    );
    if (data) {
      setForm((prev) => ({
        ...prev,
        repName: data.full_name,
        repPhone: data.phone,
      }));
      toast.success("Đã tìm thấy Người đại diện");
    }
  };

  const handleBlurManagerCode = async () => {
    if (!form.managerCode) return;
    const data = await callApi(
      `https://api.bvb.cybertech.com.vn/api/v1/admin/agents/lookup/bank-staffs/${form.managerCode}`
    );
    if (data) setForm((prev) => ({ ...prev, managerName: data.staff_name }));
  };

  const handleBlurDvkdCode = async () => {
    if (!form.dvkdCode) return;
    const data = await callApi(
      `https://api.bvb.cybertech.com.vn/api/v1/admin/agents/lookup/business-unit/${form.dvkdCode}`
    );
    if (data)
      setForm((prev) => ({ ...prev, dvkdName: data.business_unit_name }));
  };

  const handleRegisterClick = () => {
    if (
      !form.cifParent ||
      !form.cifPayment ||
      !form.account ||
      !form.managerCode ||
      !form.dvkdCode
    ) {
      return toast.error("Vui lòng điền các trường bắt buộc (*)");
    }
    setIsConfirmOpen(true);
  };

  // --- SUBMIT: GỌI API ---
  const handleSubmit = async () => {
    setIsConfirmOpen(false);
    setIsLoading(true);

    // Mapping đúng key như Curl yêu cầu
    const agentData = {
      is_central: form.isCenter,
      parent_cif: form.cifParent,
      cif_number: form.cifPayment,
      agent_name: form.paymentName,
      account_number: form.account,
      registration_number: form.registrationNumber,
      address: form.address,
      representative_cif: form.repCif,
      representative_name: form.repName,
      representative_phone: form.repPhone,
      manager_code: form.managerCode,
      manager_name: form.managerName,
      business_unit_code: form.dvkdCode,
      business_unit_name: form.dvkdName,
      branch_code: form.dvkdCode,
      daily_limit: Number(form.limitDay) || 0,
      monthly_limit: Number(form.limitMonth) || 0,
    };
    console.log("Dữ liệu chuẩn bị gửi đi:", agentData); // Xem is_central ở đây là true hay false
    const formData = new FormData();
    // Backend yêu cầu field 'data' là stringified JSON
    formData.append("data", JSON.stringify(agentData));

    // Thêm files vào đúng key 'files'
    if (files.length > 0) {
      files.forEach((fileObj) => {
        formData.append("files", fileObj.file);
      });
    }

    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(
        "https://api.bvb.cybertech.com.vn/api/v1/admin/agents/create/with-documents",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            // QUAN TRỌNG: Không set Content-Type ở đây khi dùng FormData
          },
          body: formData,
        }
      );

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result.detail || "Lỗi khi tạo đại lý");
      }

      toast.success("Tạo đại lý thành công!");
      onSubmit();
      onClose();
    } catch (error) {
      console.error("Submit Error:", error);
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (files.length + selectedFiles.length > 10) {
      toast.warning("Tối đa 10 file");
      return;
    }
    const newFiles = selectedFiles.map((f) => ({
      name: f.name,
      size: f.size,
      file: f,
    }));
    setFiles((prev) => [...prev, ...newFiles]);
    e.target.value = null; // Reset input để có thể chọn lại file cũ nếu muốn
  };

  const handlePhoneInput = (key, val) => {
    if (!/^\d*$/.test(val) || val.length > 10) return;
    update(key, val);
  };

  if (!isOpen) return null;

  return (
    <div className="agent-create-overlay" onClick={onClose}>
      <div className="agent-create-modal" onClick={(e) => e.stopPropagation()}>
        <div className="agent-create-header">
          <h2>Đăng ký Đại lý mới</h2>
          <button className="agent-create-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="agent-create-body">
          <h3 className="section-title">Thông tin chung</h3>
          <div className="agent-create-grid">
            <div className="ac-field full ac-checkbox-row">
              <label className="ac-checkbox-label">
                <input
                  type="checkbox"
                  checked={form.isCenter}
                  onChange={(e) => update("isCenter", e.target.checked)}
                />
                <span>ĐẠI LÝ TRUNG TÂM</span>
              </label>
            </div>

            <div className="ac-field">
              <label>
                CIF Agent chủ quản <span className="required">*</span>
              </label>
              <div className="ac-input-icon">
                <input
                  type="text"
                  value={form.cifParent}
                  onChange={(e) => update("cifParent", e.target.value)}
                />
                <span
                  className="ac-icon-search"
                  onClick={handleLookupCifParent}
                ></span>
              </div>
            </div>
            <div className="ac-field">
              <label>Tên Agent chủ quản</label>
              <input
                type="text"
                value={form.cifParentName}
                readOnly
                style={{ backgroundColor: "#f5f5f5" }}
              />
            </div>

            <div className="ac-field">
              <label>
                CIF Đại lý thanh toán <span className="required">*</span>
              </label>
              <div className="ac-input-icon">
                <input
                  type="text"
                  value={form.cifPayment}
                  onChange={(e) => update("cifPayment", e.target.value)}
                />
                <span
                  className="ac-icon-search"
                  onClick={handleLookupCifPayment}
                ></span>
              </div>
            </div>
            <div className="ac-field">
              <label>
                Tên Đại lý thanh toán <span className="required">*</span>
              </label>
              <input
                type="text"
                value={form.paymentName}
                readOnly // Ngăn người dùng tự nhập
                style={{ backgroundColor: "#f5f5f5" }}
              />
            </div>

            <div className="ac-field">
              <label>ID Đại lý thanh toán</label>
              <input
                type="text"
                value={form.agentId}
                disabled
                style={{
                  backgroundColor: "#f5f5f5",
                  fontWeight: "600",
                }}
              />
            </div>

            <div className="ac-field">
              <label>
                Tài khoản giao dịch <span className="required">*</span>
              </label>
              <select
                value={form.account}
                onChange={(e) => {
                  const selectedAccount = e.target.value;
                  setForm(prev => ({
                    ...prev,
                    account: selectedAccount,
                    // Nếu chọn tài khoản (khác rỗng) thì mới hiển thị tên đại lý
                    paymentName: selectedAccount ? prev.tempPaymentName : ""
                  }));
                }}
              >
                <option value="">Chọn tài khoản thanh toán</option>
                {accountOptions.map((opt, idx) => (
                  <option key={idx} value={opt.account_number}>
                    {opt.account_number} - {opt.account_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="ac-field">
              <label>Trạng thái</label>
              <input
                type="text"
                value={form.status}
                disabled
                placeholder="--"
              />
            </div>
            <div className="ac-field">
              <label>Số điện thoại Đại lý</label>
              <input
                type="text"
                value={form.agentPhone}
                onChange={(e) => handlePhoneInput("agentPhone", e.target.value)}
              />
            </div>

            <div className="ac-field">
              <label>Khu vực</label>
              <select
                value={form.region}
                onChange={(e) => update("region", e.target.value)}
              >
                <option value="">Chọn khu vực</option>
                <option value="MB">Miền Bắc</option>
                <option value="MT">Miền Trung</option>
                <option value="MN">Miền Nam</option>
              </select>
            </div>
            <div className="ac-field">
              <label>
                Địa chỉ Đại lý <span className="required">*</span>
              </label>
              <input
                type="text"
                value={form.address}
                onChange={(e) => update("address", e.target.value)}
              />
            </div>

            <div className="ac-field">
              <label>CIF người đại diện</label>
              <div className="ac-input-icon">
                <input
                  type="text"
                  value={form.repCif}
                  onChange={(e) => update("repCif", e.target.value)}
                />
                <span
                  className="ac-icon-search"
                  onClick={handleLookupRepCif}
                ></span>
              </div>
            </div>
            <div className="ac-field">
              <label>
                Tên người đại diện ĐLTT <span className="required">*</span>
              </label>
              <input
                type="text"
                value={form.repName}
                onChange={(e) => update("repName", e.target.value)}
                placeholder="Nhập tên người đại diện"
              />
            </div>

            <div className="ac-field">
              <label>
                SĐT người đại diện ĐLTT <span className="required">*</span>
              </label>
              <input
                type="text"
                value={form.repPhone}
                onChange={(e) => handlePhoneInput("repPhone", e.target.value)}
                placeholder="Nhập số điện thoại"
              />
            </div>
            <div className="ac-field empty-cell"></div>

            <div className="ac-field">
              <label>
                Mã CBNV BVBank quản lý <span className="required">*</span>
              </label>
              <input
                type="text"
                value={form.managerCode}
                onChange={(e) => update("managerCode", e.target.value)}
                onBlur={handleBlurManagerCode}
              />
            </div>
            <div className="ac-field">
              <label>Tên CBNV BVBank quản lý</label>
              <input
                type="text"
                value={form.managerName}
                readOnly
                style={{ backgroundColor: "#f5f5f5" }}
              />
            </div>
            <div className="ac-field">
              <label>
                Mã ĐVKD phát triển <span className="required">*</span>
              </label>
              <input
                type="text"
                value={form.dvkdCode}
                onChange={(e) => update("dvkdCode", e.target.value)}
                onBlur={handleBlurDvkdCode}
              />
            </div>
            <div className="ac-field">
              <label>Tên ĐVKD phát triển</label>
              <input
                type="text"
                value={form.dvkdName}
                readOnly
                style={{ backgroundColor: "#f5f5f5" }}
              />
            </div>

            <div className="ac-field">
              <label>Người tạo</label>
              <input type="text" value={form.createdBy} readOnly />
            </div>
            <div className="ac-field">
              <label>Thời gian tạo</label>
              <input type="text" value={form.createdAt} readOnly />
            </div>
          </div>

          <h3 className="section-title">Thông tin hạn mức</h3>
          <div className="agent-create-grid">
            <div className="ac-field">
              <label>
                Hạn mức ngày <span className="required">*</span>
              </label>
              <select
                value={form.limitDay}
                onChange={(e) => update("limitDay", e.target.value)}
              >
                <option value="">Chọn hạn mức</option>
                <option value="200000000">200.000.000 VND</option>
                <option value="500000000">500.000.000 VND</option>
              </select>
            </div>
            <div className="ac-field">
              <label>
                Hạn mức tháng <span className="required">*</span>
              </label>
              <select
                value={form.limitMonth}
                onChange={(e) => update("limitMonth", e.target.value)}
              >
                <option value="">Chọn hạn mức</option>
                <option value="5000000000">5.000.000.000 VND</option>
                <option value="10000000000">10.000.000.000 VND</option>
              </select>
            </div>
          </div>

          <h3 className="section-title">Tài liệu đính kèm</h3>
          <div className="upload-block">
            <input
              type="file"
              id="create-agent-file"
              multiple
              style={{ display: "none" }}
              onChange={handleFileUpload}
            />
            <button
              className="btn-upload"
              onClick={() =>
                document.getElementById("create-agent-file").click()
              }
            >
              <img
                src="/icons/icons8-upload-100.png"
                className="edit-doc-upload-icon"
                alt=""
              />{" "}
              Tải lên tài liệu
            </button>
            <span className="upload-note">(JPG, PNG, PDF. Max 10MB)</span>
          </div>
          <table className="ac-doc-table">
            <thead>
              <tr>
                <th>STT</th>
                <th>Tên hồ sơ</th>
                <th>Dung lượng</th>
              </tr>
            </thead>
            <tbody>
              {files.map((f, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>{f.name}</td>
                  <td>
                    {(f.size / 1024 / 1024).toFixed(1)}MB
                    <span
                      className="doc-remove"
                      onClick={() =>
                        setFiles(files.filter((_, i) => i !== idx))
                      }
                    >
                      ×
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="agent-create-footer">
          <button className="btn-cancel" onClick={onClose} disabled={isLoading}>
            Quay về
          </button>
          <button
            className="btn-submit"
            onClick={handleRegisterClick}
            disabled={isLoading}
          >
            {isLoading ? "Đang xử lý..." : "Đăng ký"}
          </button>
        </div>
      </div>
      <ConfirmCreateModal
        isOpen={isConfirmOpen}
        message="Bạn có chắc chắn muốn tạo mới đại lý không?"
        onConfirm={handleSubmit}
        onCancel={() => setIsConfirmOpen(false)}
      />
    </div>
  );
}
